import { z } from "zod";
import { dateTimeSchema, enforceSameTenant, nonEmptyStringSchema, tenantOwnedShape, } from "./common.js";
import { processingJobIdSchema, tenantIdSchema } from "./ids.js";
import { processingStateSchema } from "./vocabulary.js";
// Must list every value of the app_private.job_type database enum. A missing
// member is not cosmetic: any surface that validates a queue row against this
// schema rejects the whole payload, which is how an absent `media_understanding`
// took the control plane down the first time a media job existed.
//
// It happened a second time, the same way. `document_extraction` was added to
// the database enum (20260726120000), used by an applied migration, and listed
// in `apps/workers/src/contracts.ts` — and left out here. The lane had produced
// no job yet, so the fuse was lit but had not burned: the first queued
// attachment would have failed control-plane validation on the whole response.
// Three independent lists is two too many; the exact-set parity gate that
// compares them lives in the database self-check.
export const processingJobTypeSchema = z.enum([
    "source_inventory",
    "source_graph",
    "canonical_extraction",
    "document_extraction",
    "media_understanding",
    "header_projection",
    "knowledge_extraction",
    "fact_resolution",
    "projection_refresh",
    "coverage_reconciliation",
    "conversation_rollup",
]);
export const processingTargetRefSchema = z
    .object({
    tenantId: tenantIdSchema,
    recordType: z.enum([
        "connection",
        "sync_run",
        "source_item",
        "document_version",
        "entity",
        "claim",
        "projection",
    ]),
    recordId: z.string().uuid(),
})
    .strict();
/**
 * Ceiling on a queue row's idempotency key.
 *
 * Derived, not chosen. The key's job is identity, and identity is as long as
 * whatever it identifies — so the bound has to come from the widest producer,
 * not from a round number that felt safe. The widest is the self-heal lane,
 * which names a job `heal-canonical:<source item>:<record key>`: 15 + 36 + 1 =
 * 52 characters of frame, plus a record key. When the record key belongs to a
 * conversation window it is six `U+001F` fields, and each part is capped at 512
 * by `conversationKeyPartSchema` — worst case about 637, which puts today's
 * true ceiling near 689. Staging's longest live key is 261.
 *
 * 1024 clears that ceiling with room and stays far under the 4096 of
 * `nonEmptyStringSchema`, so a key can grow into it without the schema having
 * to move again. The bound is a guard against unbounded growth, never a reason
 * to shorten a key: a shortened key is a different job, and the queue rows it
 * was standing for come back as new work under a new identity.
 */
export const PROCESSING_IDEMPOTENCY_KEY_MAX_LENGTH = 1024;
/**
 * A queue idempotency key: bounded, and free of every control character except
 * the one this repository builds keys out of.
 *
 * `U+001F` is admitted deliberately. It is the unit separator the deterministic
 * key builders join fields with, so a key that carries one is well formed, not
 * corrupt — rejecting it is what stopped a lane of self-heal jobs from ever
 * being read. Every other C0 character and `U+007F` stay barred, because those
 * arrive from provider text rather than from a key builder, and they are what
 * would let a key break a log line or forge a field boundary.
 */
export const processingIdempotencyKeySchema = nonEmptyStringSchema
    .max(PROCESSING_IDEMPOTENCY_KEY_MAX_LENGTH)
    // eslint-disable-next-line no-control-regex -- U+001F is the key separator
    .regex(/^[^\u0000-\u001e\u007f]+$/u, {
    message: "must not carry a control character other than the key separator",
});
export const processingJobSchema = z
    .object({
    ...tenantOwnedShape,
    id: processingJobIdSchema,
    jobType: processingJobTypeSchema,
    targetRef: processingTargetRefSchema,
    idempotencyKey: processingIdempotencyKeySchema,
    operationVersion: nonEmptyStringSchema.max(128),
    status: processingStateSchema,
    attempt: z.number().int().nonnegative(),
    maxAttempts: z.number().int().positive().max(100),
    availableAt: dateTimeSchema,
    leaseOwner: nonEmptyStringSchema.max(256).nullable(),
    leaseExpiresAt: dateTimeSchema.nullable(),
    finishedAt: dateTimeSchema.nullable(),
    errorCode: nonEmptyStringSchema.max(256).nullable(),
    createdAt: dateTimeSchema,
    updatedAt: dateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    enforceSameTenant(value.tenantId, [{ tenantId: value.targetRef.tenantId, path: ["targetRef"] }], context);
    if (value.attempt > value.maxAttempts) {
        context.addIssue({
            code: "custom",
            path: ["attempt"],
            message: "Attempt cannot exceed maxAttempts.",
        });
    }
    if (value.status === "processing" &&
        (value.leaseOwner === null || value.leaseExpiresAt === null)) {
        context.addIssue({
            code: "custom",
            path: ["leaseOwner"],
            message: "Processing jobs require an active lease.",
        });
    }
    if (value.status === "success" && value.finishedAt === null) {
        context.addIssue({
            code: "custom",
            path: ["finishedAt"],
            message: "Successful jobs require finishedAt.",
        });
    }
});
//# sourceMappingURL=processing.js.map